import { useEffect, useRef, useState } from "react";
import { Download, Send, RefreshCw, CheckCircle2, Clock3, AlertCircle } from "lucide-react";
import { SettingsButton, SettingsFeedback, type SettingsFeedbackValue } from "../../settings/OrganizationSettingsUI";
import { coreCall } from "../../../lib/services/gatewayService";
import { useSettingsStore } from "../../../lib/stores/settingsStore";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import { customerOrderSettings } from "../../../lib/utils/customerOrderSettings";
import { useCustomerOrderTemplates } from "../../../lib/hooks/useCustomerOrderTemplates";
import { CustomerOrderTemplatePicker } from "./CustomerOrderTemplatePicker";
export function CustomerOrderDocumentActions({
  orderId,
  status,
}: {
  orderId: string;
  status: string;
}) {
  const config = customerOrderSettings(useSettingsStore((s) => s.settings));
  const organizationId = useSettingsStore((s) => s.organizationId);
  // Con più template documento dell'org si sceglie quale usare per PDF e invio;
  // con uno solo si usa quello, senza chiedere
  const { templates } = useCustomerOrderTemplates(organizationId, config.enabled);
  const [pendingAction, setPendingAction] = useState<{ action: "pdf" | "send"; resend: boolean } | null>(null);
  const { hasPermission } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<SettingsFeedbackValue>(null);
  const setMessage = (text: string) => setFeedback({ text, tone: "error" });
  const [requests, setRequests] = useState<any[]>([]);
  const resendKey = useRef<string | null>(null);
  const refresh = async () => {
    const { data, error } = await coreCall<any>("orders/warehouse-document", {
      body: { order_id: orderId, action: "status" },
    });
    if (error) throw new Error(error.message);
    setRequests(data?.requests || []);
  };
  useEffect(() => {
    let active = true;
    if (config.enabled)
      void refresh().catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [orderId, status, config.enabled]);
  const run = async (
    action: "pdf" | "send" | "retry",
    resend = false,
    requestId?: string,
    templateId?: string,
  ) => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      if (resend && !resendKey.current) resendKey.current = crypto.randomUUID();
      const { data, error } = await coreCall<any>("orders/warehouse-document", {
        body: {
          order_id: orderId,
          action,
          ...(resend ? { resend_key: resendKey.current } : {}),
          ...(requestId ? { request_id: requestId } : {}),
          ...(templateId ? { template_id: templateId } : {}),
        },
      });
      if (error) throw new Error(error.message);
      if (action === "pdf") {
        const bytes = Uint8Array.from(atob(data.pdf_base64), (c) =>
          c.charCodeAt(0),
        );
        const url = URL.createObjectURL(
          new Blob([bytes], { type: "application/pdf" }),
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setFeedback({ text: "PDF generato e archiviato", tone: "success" });
      } else {
        if (resend) resendKey.current = null;
        setFeedback({ text: "Richiesta registrata. L’esito comparirà nello storico invii.", tone: "info" });
        await refresh();
      }
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };
  // PDF e invio: con più template chiede prima quale usare. «Riprova» invece
  // riusa il template già salvato sulla richiesta.
  const start = (action: "pdf" | "send", resend = false) => {
    if (busy) return;
    if (templates.length > 1) setPendingAction({ action, resend });
    else void run(action, resend);
  };
  if (!config.enabled || !hasPermission("inventory", "view")) return null;
  const sent = requests.some((r) => r.email_status === "sent");
  const labels: Record<string, string> = {
    pending: "Da preparare",
    processing: "Preparazione PDF",
    queued: "In coda",
    ready_to_send: "Pronto",
    sending: "Invio in corso",
    sent: "Inviato",
    failed: "Errore",
    cancelled: "Annullato",
  };
  return (
    <section className="org-settings org-settings-document" aria-label="Documento e invio al magazzino" aria-busy={busy}>
      <header className="org-settings-section-heading">
        <h3>Documento e invio al magazzino</h3>
        <p>{config.recipients.length ? config.recipients.join(", ") : "Configura il destinatario nelle impostazioni ordini."}</p>
      </header>
      <div className="org-settings-actions">
        <SettingsButton disabled={busy} onClick={() => start("pdf")}><Download size={15} aria-hidden="true" />Scarica PDF</SettingsButton>
        {hasPermission("inventory", "update") && (
          <SettingsButton variant="primary" disabled={busy || ["draft", "cancelled"].includes(status) || !config.recipients.length || !config.sender_identity_id}
            onClick={() => start("send")}><Send size={15} aria-hidden="true" />Invia al magazzino</SettingsButton>
        )}
        {sent && hasPermission("inventory", "update") && (
          <SettingsButton disabled={busy} onClick={() => {
            if (window.confirm("Inviare nuovamente il PDF al magazzino?")) start("send", true);
          }}>Reinvia PDF</SettingsButton>
        )}
        <SettingsButton variant="ghost" disabled={busy} onClick={() => refresh().catch((e) => setMessage(e.message))}>
          <RefreshCw size={14} aria-hidden="true" />Aggiorna esiti
        </SettingsButton>
      </div>
      {status === "draft" && <p className="org-settings-description">Conferma l’ordine per inviarlo al magazzino. Puoi già scaricare il PDF.</p>}
      <SettingsFeedback value={feedback} />
      {requests.length > 0 && <ul className="org-settings-history" aria-label="Storico invii">
        {requests.map((r, i) => {
          const state = r.email_status || r.status;
          const isFailed = r.status === "failed" || r.email_status === "failed";
          const tone = isFailed ? "failed" : state === "sent" ? "sent" : ["pending", "processing", "queued", "ready_to_send", "sending"].includes(state) ? "pending" : "neutral";
          const Icon = isFailed ? AlertCircle : state === "sent" ? CheckCircle2 : Clock3;
          return <li key={`${r.id}:${i}`}>
            <span className="org-settings-recipient">{r.recipient_email || "Preparazione documento"}</span>
            <span className={`org-settings-status is-${tone}`}><Icon size={12} aria-hidden="true" />{labels[state] || state}</span>
            {r.sent_at && <time className="org-settings-description" dateTime={r.sent_at}>{new Date(r.sent_at).toLocaleString("it-IT")}</time>}
            {isFailed && hasPermission("inventory", "update") && <SettingsButton variant="ghost" disabled={busy}
              onClick={() => run("retry", false, r.id)}>Riprova</SettingsButton>}
            {(r.last_error || r.error_message) && <span className="org-settings-history-error">{r.last_error || r.error_message}</span>}
          </li>;
        })}
      </ul>}
      <CustomerOrderTemplatePicker
        isOpen={!!pendingAction}
        title={pendingAction?.action === "pdf" ? "Scarica PDF" : "Invia al magazzino"}
        templates={templates}
        defaultTemplateId={config.default_template_id}
        onSelect={(t) => {
          const p = pendingAction;
          setPendingAction(null);
          if (p) void run(p.action, p.resend, undefined, t.id);
        }}
        onClose={() => setPendingAction(null)}
      />
    </section>
  );
}
